import axios from "axios";
import { useState } from "react";
import {
  CheckCircle,
  Eye,
  EyeSlash,
  Lock,
  Send,
  XCircle,
} from "react-bootstrap-icons";

import PageHeader from "../../components/PageHeader";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";

import "./ClientSettingsPage.css";

export default function ClientSettingsPage() {
  const { user, dispatch } = useAuthContext();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [passwordErr, setPasswordErr] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [securityErr, setSecurityErr] = useState("");
  const [securitySuccess, setSecuritySuccess] = useState("");
  const [securityLoading, setSecurityLoading] = useState(false);

  const [deactivateErr, setDeactivateErr] = useState("");
  const [deactivateSuccess, setDeactivateSuccess] = useState("");
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  const logoutClient = () => {
    localStorage.removeItem("clientUser");
    dispatch({ type: "LOGOUT", payload: {} });
  };

  const changePassword = async (e) => {
    e.preventDefault();

    setPasswordErr("");
    setPasswordSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordErr("Please complete all password fields.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordErr("Your new password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordErr("The new passwords do not match.");
      return;
    }

    try {
      setPasswordLoading(true);

      await axios.put(
        `${ApiConnectionString}/api/client/settings/password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Your password has been changed.");
    } catch (error) {
      console.error(error);

      if (typeof error.response?.data === "string") {
        setPasswordErr(error.response.data);
      } else {
        setPasswordErr("Could not change password.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const signOutAllDevices = async () => {
    setSecurityErr("");
    setSecuritySuccess("");

    try {
      setSecurityLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/client/settings/sign-out-all-devices`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setSecuritySuccess("You have been signed out of all devices.");
      logoutClient();
    } catch (error) {
      console.error(error);

      if (typeof error.response?.data === "string") {
        setSecurityErr(error.response.data);
      } else {
        setSecurityErr("Could not sign out of all devices.");
      }
    } finally {
      setSecurityLoading(false);
    }
  };

  const requestDeactivatePortal = async () => {
    setDeactivateErr("");
    setDeactivateSuccess("");

    try {
      setDeactivateLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/client/settings/deactivate-portal-request`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setDeactivateSuccess("Your request has been sent to your therapist.");
    } catch (error) {
      console.error(error);

      if (typeof error.response?.data === "string") {
        setDeactivateErr(error.response.data);
      } else {
        setDeactivateErr("Could not send your request.");
      }
    } finally {
      setDeactivateLoading(false);
    }
  };

  return (
    <div className="client-settings-page">
      <PageHeader
        title="Settings"
        text="Manage your client portal security and access."
      />

      <div className="client-settings-grid">
        <section className="client-settings-card">
          <div className="client-settings-header">
            <div className="client-settings-icon">
              <Lock />
            </div>

            <div>
              <h2>Change password</h2>
              <p>Update the password you use to access your client portal.</p>
            </div>
          </div>

          {passwordErr && (
            <div className="client-settings-error">
              <XCircle />
              <span>{passwordErr}</span>
            </div>
          )}

          {passwordSuccess && (
            <div className="client-settings-success">
              <CheckCircle />
              <span>{passwordSuccess}</span>
            </div>
          )}

          <form onSubmit={changePassword}>
            <div className="client-settings-field">
              <label>Current password</label>

              <div className="client-settings-password">
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />

                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  {showCurrent ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="client-settings-field">
              <label>New password</label>

              <div className="client-settings-password">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                >
                  {showNew ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="client-settings-field">
              <label>Confirm new password</label>

              <div className="client-settings-password">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="client-settings-primary-btn"
              disabled={passwordLoading}
            >
              {passwordLoading ? "Saving..." : "Change password"}
            </button>
          </form>
        </section>

        <section className="client-settings-card">
          <div className="client-settings-header">
            <div className="client-settings-icon">
              <Send />
            </div>

            <div>
              <h2>Sign out of all devices</h2>
              <p>This will sign you out everywhere, including this device.</p>
            </div>
          </div>

          {securityErr && (
            <div className="client-settings-error">
              <XCircle />
              <span>{securityErr}</span>
            </div>
          )}

          {securitySuccess && (
            <div className="client-settings-success">
              <CheckCircle />
              <span>{securitySuccess}</span>
            </div>
          )}

          <button
            type="button"
            className="client-settings-secondary-btn"
            disabled={securityLoading}
            onClick={signOutAllDevices}
          >
            {securityLoading ? "Signing out..." : "Sign out of all devices"}
          </button>
        </section>

        <section className="client-settings-card client-settings-danger-card">
          <div className="client-settings-header">
            <div className="client-settings-danger-icon">
              <XCircle />
            </div>

            <div>
              <h2>Deactivate portal access</h2>
              <p>
                Send a request to your therapist to disable your client portal
                access.
              </p>
            </div>
          </div>

          {deactivateErr && (
            <div className="client-settings-error">
              <XCircle />
              <span>{deactivateErr}</span>
            </div>
          )}

          {deactivateSuccess && (
            <div className="client-settings-success">
              <CheckCircle />
              <span>{deactivateSuccess}</span>
            </div>
          )}

          <button
            type="button"
            className="client-settings-danger-btn"
            disabled={deactivateLoading}
            onClick={requestDeactivatePortal}
          >
            {deactivateLoading
              ? "Sending request..."
              : "Request deactivation"}
          </button>
        </section>
      </div>
    </div>
  );
}